// src/disc/report/discLiterature.js
//
// DISC literatür katmanı (2026-08-17).
//
// Rapor motoru şimdiye kadar yalnız "sıra + bant" okuyordu. Bu modül, klasik DISC
// literatürünün iki temel okuma aracını ekler ve motoru harf sıralamasının ötesine
// taşır:
//
//   1) MARSTON EKSENLERİ — Marston'ın (Emotions of Normal People, 1928) iki
//      soruya dayanan çerçevesi: kişi ortamı elverişli mi düşman mı algılıyor,
//      ve kendini ortamdan güçlü mü güçsüz mü görüyor. Pratikte iki eksene döner:
//        · İş/görev odaklı (D+C)  ↔  insan odaklı (I+S)
//        · Dışa dönük/eylem (D+I) ↔  içe dönük/temkinli (S+C)
//      Bu, örnek rapor PDF'lerindeki çeyrek haritasının aynı eksenleridir.
//
//   2) KLASİK ÖRÜNTÜLER — DISC uygulamasında yerleşik 15 klasik profil örüntüsü
//      (Sonuç Odaklı, Geliştiren, İlham Veren, İkna Eden, Tanıtan, Değerlendiren,
//      Danışman, Temsilci, Uzman, Mükemmeliyetçi, Nesnel Düşünür, Araştırmacı,
//      Yaratıcı, Başaran, Uygulayıcı).
//
// ÖRÜNTÜ EŞLEŞTİRME NEDEN "MERKEZLENMİŞ" YAPILIYOR:
// Klasik örüntüler profilin YÜKSEKLİĞİYLE değil BİÇİMİYLE ilgilidir. Dördü de 80
// olan bir profil "Sonuç Odaklı" değildir; sadece her yerde yüksektir. Bu yüzden
// hem kişinin hem prototipin vektöründen kendi ortalaması çıkarılır (merkezleme),
// sonra kosinüs benzerliği hesaplanır. Böylece yalnız biçim karşılaştırılır ve
// düz profiller doğal olarak "örüntüsüz" çıkar — uydurma bir örüntüye zorlanmaz.
//
// Örüntü hem UYARLANMIŞ hem DOĞAL grafik için ayrı hesaplanır; ikisinin farklı
// çıkması raporun en güçlü kişiselleştirme kaynağıdır.

export const HARFLER = ["D", "I", "S", "C"];

// ── Klasik örüntü prototipleri ──────────────────────────────────────────────
// Değerler mutlak skor hedefi değil, BİÇİM tarifidir (merkezlenerek kullanılır).
export const KLASIK_ORUNTULER = [
  {
    key: "resultOriented", vektor: { D: 90, I: 40, S: 25, C: 35 },
    tr: { ad: "Sonuç Odaklı", ozet: "Hedefe en kısa yoldan gitme ve sonucu kişisel olarak sahiplenme eğilimi belirgindir.", guc: "Belirsizlikte karar üretir, sorumluluğu üstlenir, işi tıkanmaktan çıkarır.", gerilim: "Hız ve kontrol ihtiyacı, katılımı ve farklı görüşü daraltabilir.", rol: "Kriz yönetimi, dönüşüm, hızlı karar gerektiren sahalar." },
    en: { ad: "Result-Oriented", ozet: "A strong pull toward the shortest route to the goal and personal ownership of the outcome.", guc: "Produces decisions under uncertainty, takes responsibility, unblocks work.", gerilim: "The need for speed and control can narrow participation and dissent.", rol: "Crisis handling, turnaround, fast-decision environments." }
  },
  {
    key: "developer", vektor: { D: 85, I: 45, S: 30, C: 45 },
    tr: { ad: "Geliştiren", ozet: "Kendi alanını kurma, bağımsız çalışma ve yeni sorumluluk üstlenme eğilimi öndedir.", guc: "Özerk ilerler, yeni alan açar, engeli kendi çözer.", gerilim: "Bağımsızlık, ekip ritmini ve ortak süreci geride bırakabilir.", rol: "Yeni iş kurma, sıfırdan alan açma, özerk sorumluluk." },
    en: { ad: "Developer", ozet: "Builds their own territory, works independently and takes on new responsibility.", guc: "Moves autonomously, opens new ground, removes obstacles personally.", gerilim: "Independence can outpace team rhythm and shared process.", rol: "New ventures, greenfield work, autonomous ownership." }
  },
  {
    key: "inspirational", vektor: { D: 80, I: 75, S: 30, C: 35 },
    tr: { ad: "İlham Veren", ozet: "Yön verme ile ikna etmeyi birleştirir; insanları bir hedefin etrafında hareket ettirir.", guc: "Vizyonu görünür kılar, enerji yaratır, kararı arkasına insan toplayarak taşır.", gerilim: "Etkileme gücü, ayrıntı ve takip disiplininin önüne geçebilir.", rol: "Değişim iletişimi, sahne gerektiren liderlik, paydaş seferberliği." },
    en: { ad: "Inspirational", ozet: "Combines direction with persuasion, moving people around a goal.", guc: "Makes vision visible, creates energy, carries decisions with people behind them.", gerilim: "Influence can outrun detail and follow-through.", rol: "Change communication, front-stage leadership, stakeholder mobilisation." }
  },
  {
    key: "persuader", vektor: { D: 70, I: 80, S: 40, C: 45 },
    tr: { ad: "İkna Eden", ozet: "İşi insanlarla ve insanlar üzerinden yürütür; ilişki ile sonucu birlikte tutar.", guc: "Zor konuları ilişkiyi bozmadan açar, uzlaşma üretir.", gerilim: "Onay ihtiyacı, net sınır koymayı geciktirebilir.", rol: "Satış, müzakere, iş geliştirme, paydaş yönetimi." },
    en: { ad: "Persuader", ozet: "Works with and through people, holding relationship and result together.", guc: "Raises hard topics without breaking the relationship; builds agreement.", gerilim: "The need for approval can delay firm boundaries.", rol: "Sales, negotiation, business development, stakeholder management." }
  },
  {
    key: "promoter", vektor: { D: 45, I: 90, S: 45, C: 30 },
    tr: { ad: "Tanıtan", ozet: "Fikri, işi ve insanları görünür kılma eğilimi profilin merkezindedir.", guc: "Bağ kurar, ağ genişletir, ortama enerji ve iyimserlik taşır.", gerilim: "Görünürlük ve heyecan, ölçüm ve kapanış disiplinini gölgeleyebilir.", rol: "Topluluk kurma, tanıtım, ilişki ağı gerektiren roller." },
    en: { ad: "Promoter", ozet: "Centres on making ideas, work and people visible.", guc: "Connects, widens the network, brings energy and optimism.", gerilim: "Visibility and excitement can overshadow measurement and closure.", rol: "Community building, promotion, network-heavy roles." }
  },
  {
    key: "appraiser", vektor: { D: 65, I: 75, S: 40, C: 60 },
    tr: { ad: "Değerlendiren", ozet: "Rekabetçi hedef takibi ile insan tarafını birlikte yönetir; sonucu kalite ölçütüyle bağlar.", guc: "Hedefi insanla birlikte kurar, ilerlemeyi ölçer, geri bildirimi somutlar.", gerilim: "Aynı anda hız, ilişki ve standart tutma çabası yorgunluk üretebilir.", rol: "Proje liderliği, performans yönetimi, çok paydaşlı yürütme." },
    en: { ad: "Appraiser", ozet: "Manages competitive goal pursuit alongside the people dimension, anchored to quality.", guc: "Sets goals with people, measures progress, makes feedback concrete.", gerilim: "Holding speed, relationship and standard at once can be tiring.", rol: "Project leadership, performance management, multi-stakeholder delivery." }
  },
  {
    key: "counselor", vektor: { D: 35, I: 75, S: 75, C: 40 },
    tr: { ad: "Danışman", ozet: "Sıcaklık ve süreklilik birlikte yüksektir; insanların açılabildiği bir alan kurar.", guc: "Dinler, güven verir, çatışmayı yumuşatır, uzun ilişki kurar.", gerilim: "Uyumu koruma isteği, zor geri bildirimi ertelemeye dönüşebilir.", rol: "Koçluk, mentorluk, insan gelişimi, destek fonksiyonları." },
    en: { ad: "Counselor", ozet: "Warmth and continuity are both high; creates space where people open up.", guc: "Listens, builds trust, softens conflict, sustains long relationships.", gerilim: "Protecting harmony can turn into postponing hard feedback.", rol: "Coaching, mentoring, people development, support functions." }
  },
  {
    key: "agent", vektor: { D: 35, I: 65, S: 80, C: 45 },
    tr: { ad: "Temsilci", ozet: "Ekibin ihtiyacını okuyup taşıma ve ortamı birlikte tutma eğilimi öndedir.", guc: "Ekip içi güveni korur, aracılık eder, ortamı sakinleştirir.", gerilim: "Herkesi memnun etme çabası kendi önceliğini görünmez kılabilir.", rol: "Takım koordinasyonu, İK ortaklığı, hizmet ve destek rolleri." },
    en: { ad: "Agent", ozet: "Reads and carries the team's needs, holding the environment together.", guc: "Protects trust, mediates, calms the environment.", gerilim: "Trying to satisfy everyone can hide their own priority.", rol: "Team coordination, HR partnering, service and support roles." }
  },
  {
    key: "specialist", vektor: { D: 35, I: 50, S: 80, C: 60 },
    tr: { ad: "Uzman", ozet: "Tutarlılık ve öngörülebilirlik profilin taşıyıcı özelliğidir.", guc: "Sözünü tutar, işi aynı kalitede tekrarlar, ekipte istikrar üretir.", gerilim: "Ani değişim ve belirsizlik enerji maliyeti yaratabilir.", rol: "Uzmanlık alanları, sürekli işleyiş, kalite gerektiren tekrarlı süreçler." },
    en: { ad: "Specialist", ozet: "Consistency and predictability carry the profile.", guc: "Keeps commitments, repeats work at the same quality, stabilises the team.", gerilim: "Sudden change and ambiguity carry an energy cost.", rol: "Deep expertise, steady operations, quality-critical repeatable processes." }
  },
  {
    key: "perfectionist", vektor: { D: 35, I: 35, S: 70, C: 85 },
    tr: { ad: "Mükemmeliyetçi", ozet: "Doğruluk ve sistem birlikte yüksektir; iş bir standarda bağlanmadan bitmiş sayılmaz.", guc: "Hatayı erken görür, standardı korur, süreci sağlamlaştırır.", gerilim: "Kanıt arayışı karar ve teslim hızını sınırlayabilir.", rol: "Kalite, denetim, uyum, süreç tasarımı." },
    en: { ad: "Perfectionist", ozet: "Accuracy and system are both high; work is not done until it meets a standard.", guc: "Catches errors early, protects the standard, hardens the process.", gerilim: "The search for evidence can limit decision and delivery speed.", rol: "Quality, audit, compliance, process design." }
  },
  {
    key: "objectiveThinker", vektor: { D: 40, I: 25, S: 55, C: 85 },
    tr: { ad: "Nesnel Düşünür", ozet: "Duygusal gürültüden arındırılmış, veriye dayalı okuma eğilimi belirgindir.", guc: "Mantık kurar, varsayımı test eder, riski önceden adlandırır.", gerilim: "Mesafeli ton, katkının fark edilmesini zorlaştırabilir.", rol: "Analiz, araştırma, risk değerlendirme, teknik değerlendirme." },
    en: { ad: "Objective Thinker", ozet: "A data-led reading, stripped of emotional noise.", guc: "Builds logic, tests assumptions, names risk early.", gerilim: "A distant tone can make the contribution harder to notice.", rol: "Analysis, research, risk assessment, technical evaluation." }
  },
  {
    key: "investigator", vektor: { D: 55, I: 25, S: 65, C: 80 },
    tr: { ad: "Araştırmacı", ozet: "Sebat ve doğruluk birlikte çalışır; iş sonuna kadar araştırılıp kapatılır.", guc: "Konuyu derinlemesine takip eder, gevşek uçları kapatır.", gerilim: "Sessiz ısrar, dışarıdan katılık gibi okunabilir.", rol: "İnceleme, denetim, kök neden analizi, uzun soluklu teknik iş." },
    en: { ad: "Investigator", ozet: "Persistence and accuracy work together; the matter is pursued to closure.", guc: "Follows topics in depth, closes loose ends.", gerilim: "Quiet insistence can read as rigidity from outside.", rol: "Investigation, audit, root-cause analysis, long-cycle technical work." }
  },
  {
    key: "creative", vektor: { D: 80, I: 30, S: 30, C: 80 },
    tr: { ad: "Yaratıcı", ozet: "Hızlı sonuç ihtiyacı ile yüksek doğruluk beklentisi aynı profilde bulunur.", guc: "Yeni çözüm üretir ve onu bir standarda bağlar; kalite ile hızı birlikte ister.", gerilim: "Bu iki ihtiyaç kişide iç gerilim ve sabırsızlık üretebilir.", rol: "Ürün tasarımı, yeniden yapılandırma, teknik yenilik." },
    en: { ad: "Creative", ozet: "A need for fast results sits alongside a high accuracy expectation.", guc: "Produces new solutions and anchors them to a standard.", gerilim: "These two needs can create internal tension and impatience.", rol: "Product design, restructuring, technical innovation." }
  },
  {
    key: "achiever", vektor: { D: 75, I: 50, S: 55, C: 75 },
    tr: { ad: "Başaran", ozet: "Kendi hedefini kendi koyar ve kendi standardıyla ölçer.", guc: "Yüksek kişisel sorumluluk, tutarlı üretim, işi bitirme disiplini.", gerilim: "İşi devretmekte zorlanabilir; kendi ölçütü ekibinkinden yüksek kalabilir.", rol: "Bireysel uzman katkı, sahiplik gerektiren yürütme." },
    en: { ad: "Achiever", ozet: "Sets their own goals and measures them by their own standard.", guc: "High personal responsibility, consistent output, finishing discipline.", gerilim: "May struggle to delegate; personal standard can exceed the team's.", rol: "Individual expert contribution, ownership-heavy delivery." }
  },
  {
    key: "practitioner", vektor: { D: 50, I: 55, S: 60, C: 70 },
    tr: { ad: "Uygulayıcı", ozet: "Bilgi ve beceriyi günlük uygulamaya çevirme eğilimi öndedir; dengeli bir profildir.", guc: "Öğrendiğini işe döker, ayrıntı ile ilişkiyi birlikte yönetir.", gerilim: "Her alanda yeterli olma çabası odak dağıtabilir.", rol: "Uzman uygulayıcı roller, eğitim, danışmanlık, saha uygulaması." },
    en: { ad: "Practitioner", ozet: "Turns knowledge and skill into daily practice; a balanced profile.", guc: "Converts learning into work, manages detail and relationship together.", gerilim: "Trying to be competent everywhere can scatter focus.", rol: "Expert practitioner roles, training, consulting, field application." }
  }
];

// ── Yardımcılar ─────────────────────────────────────────────────────────────
function ortalama(v) {
  return HARFLER.reduce((t, k) => t + (Number(v[k]) || 0), 0) / HARFLER.length;
}

function merkezle(v) {
  const o = ortalama(v);
  return HARFLER.map((k) => (Number(v[k]) || 0) - o);
}

function norm(a) {
  return Math.sqrt(a.reduce((t, x) => t + x * x, 0));
}

/**
 * Profilin BİÇİM gücü: merkezlenmiş vektörün büyüklüğü.
 * 0'a yakınsa profil düzdür ve klasik örüntü ataması anlamsızdır.
 */
export function bicimGucu(scores) {
  return Math.round(norm(merkezle(scores)));
}

/**
 * Klasik örüntü eşleştirme. Biçim gücü eşiğin altındaysa örüntü döndürmez —
 * düz profile zorla örüntü atamak, raporun en kolay yalan söyleme biçimidir.
 */
export function oruntuBul(scores, language = "tr", esik = 12) {
  const lang = language === "en" ? "en" : "tr";
  const kisi = merkezle(scores);
  const guc = norm(kisi);

  if (guc < esik) {
    return {
      key: "balanced",
      yakinlik: 0,
      bicimGucu: Math.round(guc),
      duz: true,
      ad: lang === "tr" ? "Dengeli / Örüntüsüz" : "Balanced / No dominant pattern",
      ozet: lang === "tr"
        ? "Dört sinyal birbirine yakın olduğu için klasik bir örüntüye oturmuyor; davranış tercihi bağlama göre değişiyor olabilir."
        : "The four signals sit close together, so no classical pattern applies; behaviour may be selected by context.",
      guc: lang === "tr" ? "Bağlama göre kanal değiştirebilme esnekliği." : "Flexibility to switch channels by context.",
      gerilim: lang === "tr" ? "Hangi davranışın öne alınacağı belirsiz kalırsa karşı taraf yön okuyamayabilir." : "If the leading behaviour stays unclear, others may not read direction.",
      rol: lang === "tr" ? "Çok yönlü, değişken talepli roller." : "Versatile roles with varied demands."
    };
  }

  let enIyi = null;
  for (const o of KLASIK_ORUNTULER) {
    const p = merkezle(o.vektor);
    const benzerlik = kisi.reduce((t, x, i) => t + x * p[i], 0) / (norm(kisi) * norm(p) || 1);
    if (!enIyi || benzerlik > enIyi.benzerlik) enIyi = { o, benzerlik };
  }

  const m = enIyi.o[lang];
  return {
    key: enIyi.o.key,
    // kosinüs -1..1 → okunur yakınlık yüzdesi
    yakinlik: Math.max(0, Math.round(enIyi.benzerlik * 100)),
    bicimGucu: Math.round(guc),
    duz: false,
    ad: m.ad, ozet: m.ozet, guc: m.guc, gerilim: m.gerilim, rol: m.rol
  };
}

/**
 * Marston'ın iki ekseni. Skorlardan hesaplanır; örnek rapor PDF'lerindeki
 * çeyrek haritasının eksenleriyle aynıdır.
 */
export function marstonEksenleri(scores, language = "tr") {
  const lang = language === "en" ? "en" : "tr";
  const gorev = (Number(scores.D) || 0) + (Number(scores.C) || 0);
  const insan = (Number(scores.I) || 0) + (Number(scores.S) || 0);
  const disa = (Number(scores.D) || 0) + (Number(scores.I) || 0);
  const ice = (Number(scores.S) || 0) + (Number(scores.C) || 0);

  const odakFark = gorev - insan;
  const temperFark = disa - ice;
  const etiket = (fark, a, b, notr) => (Math.abs(fark) < 10 ? notr : fark > 0 ? a : b);

  return {
    gorevPuani: gorev, insanPuani: insan, disaPuani: disa, icePuani: ice,
    odakFark, temperFark,
    odak: lang === "tr"
      ? etiket(odakFark, "İş / görev odaklı", "İnsan odaklı", "Görev ve insan dengeli")
      : etiket(odakFark, "Task focused", "People focused", "Task and people balanced"),
    temper: lang === "tr"
      ? etiket(temperFark, "Dışa dönük / eylem", "İçe dönük / temkinli", "Eylem ve temkin dengeli")
      : etiket(temperFark, "Outward / action", "Reserved / measured", "Action and caution balanced"),
    ceyrek: lang === "tr"
      ? `${etiket(odakFark, "iş/görev", "insan", "dengeli")} × ${etiket(temperFark, "dışa dönük", "içe dönük", "dengeli")}`
      : `${etiket(odakFark, "task", "people", "balanced")} × ${etiket(temperFark, "outward", "reserved", "balanced")}`
  };
}

/**
 * Uyarlama enerjisi: doğal ile uyarlanmış arasındaki toplam hareket.
 * DISC uygulamasında iki grafiğin farkı, kişinin bulunduğu bağlamda davranışını
 * ne kadar ayarladığının göstergesi olarak okunur; büyük fark sürdürülebilirlik
 * sorusu doğurur.
 */
export function uyarlamaEnerjisi(adapted, natural, language = "tr") {
  const lang = language === "en" ? "en" : "tr";
  const farklar = HARFLER.map((k) => (Number(adapted[k]) || 0) - (Number(natural[k]) || 0));
  const toplam = farklar.reduce((t, x) => t + Math.abs(x), 0);
  const yukselen = HARFLER.filter((k, i) => farklar[i] >= 10);
  const alcalan = HARFLER.filter((k, i) => farklar[i] <= -10);

  let seviye, metin;
  if (toplam <= 20) {
    seviye = "dusuk";
    metin = lang === "tr"
      ? "Uyarlama enerjisi düşüktür: kişi mevcut bağlamda büyük ölçüde doğal hâliyle çalışıyor görünmektedir. Bu genellikle sürdürülebilir bir tablodur."
      : "Adaptation energy is low: the person appears to work largely as they naturally are. This is usually sustainable.";
  } else if (toplam <= 60) {
    seviye = "orta";
    metin = lang === "tr"
      ? "Uyarlama enerjisi ortadır: bağlam belirli davranışların ayarlanmasını istiyor, ancak profil bütünüyle değişmiş değildir."
      : "Adaptation energy is moderate: the context asks for some adjustment without reshaping the whole profile.";
  } else {
    seviye = "yuksek";
    metin = lang === "tr"
      ? "Uyarlama enerjisi yüksektir: kişi doğal eğilimlerinden belirgin biçimde farklı bir davranış seti sergiliyor olabilir. Bu tablo kısa dönemde performans üretebilir, uzun dönemde enerji maliyeti ve yorgunluk sorusu doğurur."
      : "Adaptation energy is high: the person may be showing a markedly different behaviour set from their natural tendencies. This can produce short-term performance but raises a longer-term energy-cost question.";
  }

  return { toplam, farklar, yukselen, alcalan, seviye, metin };
}
